import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useApp, LOGO_URL } from "../store";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";

export default function Header() {
  const { t, locale, setLocale, currency, setCurrency, cart } = useApp();
  const [open, setOpen] = React.useState(false);
  const [productsOpen, setProductsOpen] = React.useState(false);
  const [faqOpen, setFaqOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const loc = useLocation();

  React.useEffect(() => { setOpen(false); setProductsOpen(false); setFaqOpen(false); }, [loc.pathname]);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = ["/", "/v1", "/v2", "/v3", "/v4", "/v5"].includes(loc.pathname);
  const onDark = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${onDark ? "bg-transparent" : "bg-white/92 backdrop-blur-xl border-b border-slate-200"}`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="SharkX Electric" className={`h-8 md:h-9 w-auto ${onDark ? "invert brightness-0 contrast-100 saturate-0 invert-[1]" : ""}`} style={onDark ? { filter: "invert(1) brightness(2)" } : {}} />
        </Link>

        <nav className={`hidden lg:flex items-center gap-8 font-ui text-xs uppercase tracking-[0.2em] ${onDark ? "text-white" : "text-slate-700"}`}>
          <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
            <button className="link-underline py-2 flex items-center gap-1" data-testid="nav-products">{t.nav.products} <ChevronDown size={12} /></button>
            {productsOpen && (
              <div className="absolute top-full left-0 pt-2 min-w-[240px]">
                <div className="bg-white border border-slate-200 shadow-2xl p-2 rounded-md">
                  <Link to="/products/efoils" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-efoils">{t.nav.efoils}</Link>
                  <Link to="/products/jetboards" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-jetboards">{t.nav.jetboards}</Link>
                  <Link to="/products/SharkX-Ultra-AI-electric-jetski" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-ultra">{t.nav.ultra}</Link>
                  <Link to="/products/accessories" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-accessories">{t.nav.accessories}</Link>
                </div>
              </div>
            )}
          </div>
          <Link to="/book" className="link-underline" data-testid="nav-book">{t.nav.book}</Link>
          <Link to="/blog" className="link-underline" data-testid="nav-blog">{t.nav.blog}</Link>
          
          {/* FAQ Menu with Submenu */}
          <div className="relative" onMouseEnter={() => setFaqOpen(true)} onMouseLeave={() => setFaqOpen(false)}>
            <button className="link-underline py-2 flex items-center gap-1" data-testid="nav-faq">{t.nav.faq || "FAQ"} <ChevronDown size={12} /></button>
            {faqOpen && (
              <div className="absolute top-full left-0 pt-2 min-w-[240px]">
                <div className="bg-white border border-slate-200 shadow-2xl p-2 rounded-md">
                  <Link to="/support" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-faq-support">{t.nav.faq} &amp; {t.nav.support}</Link>
                  <Link to="/warranty" className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded" data-testid="nav-warranty">{t.nav.warranty}</Link>
                </div>
              </div>
            )}
          </div>
          
          <Link to="/contact" className="link-underline" data-testid="nav-contact">{t.nav.contact}</Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <select value={locale} onChange={(e) => setLocale(e.target.value)}
            className={`bg-transparent font-ui text-xs uppercase tracking-wider border px-2 py-1 cursor-pointer rounded ${onDark ? "text-white border-white/40" : "text-slate-700 border-slate-300"}`}
            data-testid="language-select">
            <option value="en">EN</option>
            <option value="pt">PT</option>
            <option value="es">ES</option>
          </select>
          <select value={currency} onChange={(e) => { setCurrency(e.target.value); localStorage.setItem("sharkx_currency", e.target.value); }}
            className={`hidden sm:block bg-transparent font-ui text-xs uppercase tracking-wider border px-2 py-1 cursor-pointer rounded ${onDark ? "text-white border-white/40" : "text-slate-700 border-slate-300"}`}
            data-testid="currency-select">
            <option value="USD">USD</option>
            <option value="BRL">BRL</option>
          </select>

          <Link to="/cart" className={`flex items-center relative ${onDark ? "text-white" : "text-slate-900 hover:text-sky-600"}`} data-testid="cart-link">
            <ShoppingBag size={18} />
            {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-sky-500 text-white text-[10px] w-4 h-4 rounded-full grid place-items-center font-bold">{cart.length}</span>}
          </Link>
          <button className={`lg:hidden ${onDark ? "text-white" : "text-slate-900"}`} onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3 font-ui text-sm uppercase tracking-[0.18em] text-slate-800">
            <Link to="/products/efoils" className="py-2" data-testid="mnav-efoils">{t.nav.efoils}</Link>
            <Link to="/products/jetboards" className="py-2" data-testid="mnav-jetboards">{t.nav.jetboards}</Link>
            <Link to="/products/SharkX-Ultra-AI-electric-jetski" className="py-2" data-testid="mnav-ultra">{t.nav.ultra}</Link>
            <Link to="/products/accessories" className="py-2" data-testid="mnav-accessories">{t.nav.accessories}</Link>
            <Link to="/book" className="py-2" data-testid="mnav-book">{t.nav.book}</Link>
            <Link to="/blog" className="py-2" data-testid="mnav-blog">{t.nav.blog}</Link>
            
            {/* Mobile FAQ Menu */}
            <div className="py-2">
              <div className="font-semibold mb-2">{t.nav.faq || "FAQ"}</div>
              <Link to="/support" className="block py-1 pl-4 text-xs" data-testid="mnav-faq-support">{t.nav.faq} &amp; {t.nav.support || "Support"}</Link>
              <Link to="/warranty" className="block py-1 pl-4 text-xs" data-testid="mnav-warranty">{t.nav.warranty}</Link>
            </div>
            
            <Link to="/contact" className="py-2" data-testid="mnav-contact">{t.nav.contact}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
